import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import FishAvatar from '../../components/FishAvatar';
import { speak } from '../../components/TranslatableText';
import { burst } from '../../lib/confetti';
import { quizLink, TILES, type QuizState } from '../../lib/quiz';
import { supabase } from '../../lib/supabase';
import { useClassroom } from '../../state/Classroom';

// ─────────────────────────────────────────────────────────────────────────────
// The class game, for a student (/ocean/class-game; /play redirects there): join, tap an answer, see how you did.
// The teacher's screen runs the game (routes/teacher/ClassGame.tsx); this page
// follows what it broadcasts. GameInvite (below) pops up anywhere on the site
// when a game is open, so nobody has to find the page.
// ─────────────────────────────────────────────────────────────────────────────

/** The game's latest state for the open class, or null. */
export function useQuizState(): QuizState | null {
  const { active } = useClassroom();
  const link = useMemo(() => (active ? quizLink(active.id) : null), [active]);
  const [state, setState] = useState<QuizState | null>(() => link?.last ?? null);
  useEffect(() => {
    if (!link) return;
    setState(link.last);
    return link.on({ state: setState });
  }, [link]);
  return state;
}

/** "Your class game is open!": shown on every student page except /play while a game is on. */
export function GameInvite() {
  const state = useQuizState();
  const where = useLocation();
  if (!state || state.phase === 'closed' || where.pathname === '/ocean/class-game') return null;
  return (
    <Link to="/ocean/class-game" className="game-invite" role="status">
      <span aria-hidden>🎮</span>
      <span><strong>Your class game is {state.phase === 'lobby' ? 'starting' : 'on'}!</strong> Tap to join</span>
      <span aria-hidden>→</span>
    </Link>
  );
}

export default function ClassGamePlay() {
  const { active, session } = useClassroom();
  const me = session?.user.id ?? '';
  const state = useQuizState();
  const link = useMemo(() => (active ? quizLink(active.id) : null), [active]);
  const [look, setLook] = useState<unknown>(null);
  const [picked, setPicked] = useState<{ i: number; choice: number } | null>(null);
  const [deadline, setDeadline] = useState(0);
  const [now, setNow] = useState(Date.now());
  const joinedGame = useRef('');

  useEffect(() => {
    if (!me) return;
    void supabase.from('student_avatars').select('look').eq('student_id', me).maybeSingle().then(({ data }) => setLook(data?.look ?? null));
  }, [me]);

  // Say hello once per game (the teacher answers with what's on screen), and again if the page opens late.
  useEffect(() => {
    if (!link || !me) return;
    void link.send('join', { id: me });
  }, [link, me]);
  useEffect(() => {
    if (!link || !me || !state || state.phase === 'closed' || joinedGame.current === state.game) return;
    joinedGame.current = state.game;
    void link.send('join', { id: me });
  }, [link, me, state]);

  // A new question: start the clock from the time left the teacher's screen says.
  const qi = state?.phase === 'question' ? state.i : -1;
  useEffect(() => {
    if (state?.phase !== 'question') return;
    setDeadline(d => (qi !== picked?.i && d > Date.now() ? d : Date.now() + state.left * 1000));
  }, [qi]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 250);
    return () => window.clearInterval(id);
  }, []);

  // Confetti for a podium finish.
  useEffect(() => {
    if (state?.phase === 'end' && (state.ranks[me]?.rank ?? 99) <= 3) burst();
  }, [state?.phase]); // eslint-disable-line react-hooks/exhaustive-deps

  const answer = (choice: number) => {
    if (!link || state?.phase !== 'question' || picked?.i === state.i) return;
    setPicked({ i: state.i, choice });
    void link.send('answer', { id: me, game: state.game, i: state.i, choice });
  };

  if (!active) return <div className="notice">Join a class to play.</div>;

  if (!state || state.phase === 'closed') return (
    <div className="narrow game-play">
      <div className="panel game-wait">
        <FishAvatar look={look} id={me} size={90} />
        <h2>No class game right now</h2>
        <p className="muted">When your teacher starts one, it pops up on your screen.</p>
      </div>
    </div>
  );

  if (state.phase === 'lobby') return (
    <div className="narrow game-play">
      <div className="panel game-wait">
        <FishAvatar look={look} id={me} size={110} excited />
        <h2>You’re in! 🎉</h2>
        <p className="muted">Look for your fish on the big screen. The game starts soon…</p>
      </div>
    </div>
  );

  if (state.phase === 'question') {
    const mine = picked?.i === state.i ? picked.choice : null;
    const secs = Math.max(0, Math.ceil((deadline - now) / 1000));
    return (
      <div className="game-play question">
        <div className="game-play-head">
          <span className="small muted">{state.i + 1} / {state.total}</span>
          <span className="game-clock">{secs}s</span>
        </div>
        <div className="small muted">{state.q.instruction}</div>
        <div className="game-prompt small" dir="auto">
          {state.q.prompt.split('___').map((part, k, arr) => <span key={k}>{part}{k < arr.length - 1 && <span className="blank">____</span>}</span>)}
          {state.q.lang && <button type="button" className="icon" aria-label="Hear it" onClick={() => speak(state.q.prompt.replace(/___/g, ''), state.q.lang!)}>🔊</button>}
        </div>
        {state.q.hint && <div className="muted small">{state.q.hint}</div>}
        {mine == null ? (
          <div className="game-tiles play">
            {state.q.options.map((o, k) => (
              <button key={k} type="button" className="game-tile" style={{ background: TILES[k].color }} onClick={() => answer(k)} aria-label={`${TILES[k].name}: ${o}`}>
                <span className="shape" aria-hidden>{TILES[k].shape}</span><span dir="auto">{o}</span>
              </button>
            ))}
          </div>
        ) : (
          <div className="panel game-wait">
            <div className="game-locked" style={{ background: TILES[mine].color }}>{TILES[mine].shape}</div>
            <h2>Answer locked in!</h2>
            <p className="muted">Waiting for everyone… ({state.answered} of {state.players})</p>
          </div>
        )}
      </div>
    );
  }

  if (state.phase === 'reveal') {
    const r = state.results[me];
    const right = state.q.options[state.q.correct];
    return (
      <div className={`narrow game-play result ${r?.right ? 'right' : 'wrong'}`}>
        <div className="panel game-wait">
          <FishAvatar look={look} id={me} size={90} excited={r?.right} />
          <h2>{r ? (r.right ? '✅ Correct!' : '❌ Not quite') : '⏰ Out of time'}</h2>
          {r?.right && <p className="game-points">+{r.points}{r.streak >= 2 ? ` · 🔥 ${r.streak} in a row` : ''}</p>}
          {!r?.right && <p>It was <strong dir="auto">{right}</strong></p>}
          {r && <p className="muted">You’re <strong>#{r.rank}</strong> with {r.score} points</p>}
        </div>
      </div>
    );
  }

  if (state.phase === 'leaderboard') {
    const pos = state.top.findIndex(p => p.id === me);
    return (
      <div className="narrow game-play">
        <div className="panel game-wait">
          <FishAvatar look={look} id={me} size={80} />
          <h2>{pos >= 0 ? `You’re in the top 5: #${pos + 1}!` : 'Keep going!'}</h2>
          <p className="muted">Next question coming up…</p>
        </div>
      </div>
    );
  }

  const mineEnd = state.ranks[me];
  return (
    <div className="narrow game-play">
      <div className="panel game-wait">
        <FishAvatar look={look} id={me} size={120} excited />
        <h2>{mineEnd ? (mineEnd.rank === 1 ? '🏆 You won!' : mineEnd.rank <= 3 ? `🥈 #${mineEnd.rank}: on the podium!` : `You finished #${mineEnd.rank}`) : 'Game over'}</h2>
        {mineEnd && <p className="muted">{mineEnd.score} points. Great playing!</p>}
      </div>
    </div>
  );
}
